import { useState, type ReactNode } from "react";
import {
  KeyRound,
  Pencil,
  RectangleEllipsis,
  UserCheck,
  UserX,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ActionCapability, ActionReasonCode, PersonRead } from "@/api/people";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { SecretRevealDialog } from "@/components/admin/SecretRevealDialog";
import { ConfirmDialog } from "@/components/templates/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useDeactivatePerson,
  useReactivatePerson,
  useResetPersonPassword,
  useResetPersonPin,
} from "@/hooks/usePeople";
import { cn } from "@/lib/utils";

function randomPin(): string {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return String(values[0] % 10000).padStart(4, "0");
}

type ConfirmAction = "deactivate" | "reactivate" | "pin" | "password";

export interface PeopleActionControls {
  request: (action: ConfirmAction) => void;
}

interface PeopleActionsControllerProps {
  person: PersonRead;
  children: (controls: PeopleActionControls) => ReactNode;
}

export function PeopleActionsController({
  person,
  children,
}: PeopleActionsControllerProps): React.ReactElement {
  const { t } = useTranslation();
  const deactivate = useDeactivatePerson();
  const reactivate = useReactivatePerson();
  const resetPin = useResetPersonPin();
  const resetPassword = useResetPersonPassword();
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [secret, setSecret] = useState<{ kind: "pin" | "password"; value: string } | null>(null);

  const failure = (error: unknown): void => {
    showApiErrorToast(error, { fallbackMessage: t("common.apiError.fallback") });
  };

  const resetPinNow = async (): Promise<void> => {
    const pin = randomPin();
    try {
      await resetPin.mutateAsync({ id: person.id, pin });
      setSecret({ kind: "pin", value: pin });
    } catch (error) {
      failure(error);
    } finally {
      setConfirm(null);
    }
  };

  const resetPasswordNow = async (): Promise<void> => {
    try {
      const result = await resetPassword.mutateAsync(person.id);
      setSecret({ kind: "password", value: result.temporary_password });
    } catch (error) {
      failure(error);
    } finally {
      setConfirm(null);
    }
  };

  return (
    <>
      {children({ request: setConfirm })}

      <ConfirmDialog
        open={confirm === "deactivate"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("admin.users.deactivateDialog.title")}
        body={t("admin.users.deactivateDialog.body", { name: person.full_name })}
        submitLabel={t("admin.users.deactivate")}
        onConfirm={async () => {
          try {
            await deactivate.mutateAsync(person.id);
            toast.success(t("admin.users.toast.deactivated"));
            setConfirm(null);
          } catch (error) {
            failure(error);
          }
        }}
        destructive
        pending={deactivate.isPending}
        testId="deactivate-confirm-dialog"
      />
      <ConfirmDialog
        open={confirm === "reactivate"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("admin.users.reactivateDialog.title")}
        body={t("admin.users.reactivateDialog.body", { name: person.full_name })}
        submitLabel={t("admin.users.actions.reactivate")}
        onConfirm={() => {
          void reactivate
            .mutateAsync(person.id)
            .then(() => toast.success(t("admin.users.toast.reactivated")))
            .catch((error: unknown) => failure(error))
            .finally(() => setConfirm(null));
        }}
        pending={reactivate.isPending}
        testId="reactivate-confirm-dialog"
      />
      <ConfirmDialog
        open={confirm === "pin"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("admin.users.resetPinConfirm.title")}
        body={t("admin.users.resetPinConfirm.body", { name: person.full_name })}
        submitLabel={t("admin.users.resetPinConfirm.submit")}
        onConfirm={resetPinNow}
        destructive
        pending={resetPin.isPending}
        testId="reset-pin-confirm-dialog"
      />
      <ConfirmDialog
        open={confirm === "password"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("admin.users.resetPasswordConfirm.title")}
        body={t("admin.users.resetPasswordConfirm.body", { name: person.full_name })}
        submitLabel={t("admin.users.resetPasswordConfirm.submit")}
        onConfirm={resetPasswordNow}
        destructive
        pending={resetPassword.isPending}
        testId="reset-password-confirm-dialog"
      />
      <SecretRevealDialog
        open={secret?.kind === "pin"}
        onOpenChange={(open) => !open && setSecret(null)}
        title={t("admin.users.resetPinDialog.title")}
        subtitle={t("admin.users.resetPinDialog.subtitle", {
          name: person.full_name,
          tab: person.tab_number ?? "—",
        })}
        cautionText={t("admin.users.resetPinDialog.warning")}
        secret={secret?.kind === "pin" ? secret.value : null}
        display="pin"
        copyLabel={t("admin.users.resetPinDialog.copy")}
        copiedLabel={t("admin.users.resetPinDialog.copied")}
        doneLabel={t("admin.users.resetPinDialog.done")}
        testIdPrefix="reset-pin"
      />
      <SecretRevealDialog
        open={secret?.kind === "password"}
        onOpenChange={(open) => !open && setSecret(null)}
        title={t("admin.users.resetPasswordDialog.title")}
        subtitle={t("admin.users.resetPasswordDialog.subtitle", { name: person.full_name })}
        cautionText={t("admin.users.resetPasswordDialog.warning")}
        secret={secret?.kind === "password" ? secret.value : null}
        display="mono"
        copyLabel={t("admin.users.resetPasswordDialog.copy")}
        copiedLabel={t("admin.users.resetPasswordDialog.copied")}
        doneLabel={t("admin.users.resetPasswordDialog.done")}
        testIdPrefix="reset-password"
      />
    </>
  );
}

interface PeopleActionRowProps {
  person: PersonRead;
  controls: PeopleActionControls;
  editHref?: string;
  onEdit?: () => void;
  showEdit?: boolean;
  className?: string;
}

export function PeopleActionRow({
  person,
  controls,
  editHref,
  onEdit,
  showEdit = true,
  className,
}: PeopleActionRowProps): React.ReactElement | null {
  const { t } = useTranslation();
  const caps = person.action_capabilities;
  const reason = (code: ActionReasonCode): string => t(`admin.users.actionReasons.${code}`);
  const items = [
    ...(showEdit
      ? [
          {
            capability: caps.edit,
            label: t("admin.users.actions.edit"),
            testId: `admin-user-edit-${person.id}`,
            icon: Pencil,
            href: editHref,
            onClick: onEdit,
          },
        ]
      : []),
    {
      capability: caps.deactivate,
      label: t("admin.users.deactivate"),
      testId: `admin-user-deactivate-${person.id}`,
      icon: UserX,
      iconClassName: "text-destructive",
      onClick: () => controls.request("deactivate"),
    },
    {
      capability: caps.reactivate,
      label: t("admin.users.actions.reactivate"),
      testId: `admin-user-reactivate-${person.id}`,
      icon: UserCheck,
      iconClassName: "text-[var(--tone-success-fg)]",
      onClick: () => controls.request("reactivate"),
    },
    {
      capability: caps.reset_pin,
      label: t("admin.users.actions.resetPin"),
      testId: `admin-user-reset-pin-${person.id}`,
      icon: KeyRound,
      onClick: () => controls.request("pin"),
    },
    {
      capability: caps.reset_password,
      label: t("admin.users.actions.resetPassword"),
      testId: `admin-user-reset-password-${person.id}`,
      icon: RectangleEllipsis,
      onClick: () => controls.request("password"),
    },
  ].filter((item) => item.capability.state !== "hidden");

  if (items.length === 0) return null;
  return (
    <div
      className={cn("flex flex-wrap items-center gap-1", className)}
      data-testid={`people-action-row-${person.id}`}
    >
      {items.map((item) => (
        <ActionIconButton
          key={item.testId}
          capability={item.capability}
          label={item.label}
          reason={item.capability.reason_code ? reason(item.capability.reason_code) : null}
          testId={item.testId}
          icon={item.icon}
          iconClassName={item.iconClassName}
          href={item.href}
          onClick={item.onClick}
        />
      ))}
    </div>
  );
}

interface ActionIconButtonProps {
  capability: ActionCapability;
  label: string;
  reason: string | null;
  testId: string;
  icon: LucideIcon;
  iconClassName?: string;
  href?: string;
  onClick?: () => void;
}

function ActionIconButton({
  capability,
  label,
  reason,
  testId,
  icon: Icon,
  iconClassName,
  href,
  onClick,
}: ActionIconButtonProps): React.ReactElement {
  const disabled = capability.state === "disabled";
  const content = disabled && reason ? `${label}: ${reason}` : label;
  const button =
    href && !disabled ? (
      <Button asChild variant="ghost" size="icon">
        <a href={href} data-testid={testId} aria-label={label}>
          <Icon className={iconClassName} aria-hidden="true" />
        </a>
      </Button>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-testid={testId}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        <Icon className={iconClassName} aria-hidden="true" />
      </Button>
    );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabled ? (
          <span
            className="inline-flex"
            tabIndex={0}
            aria-disabled="true"
            aria-label={reason ? `${label}. ${reason}` : label}
          >
            {button}
          </span>
        ) : (
          button
        )}
      </TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  );
}
